import React, { useMemo, useState } from "react";

import { COMPARE_METRICS, CROP_SOIL_METRICS, LOCATION_SOIL_METRICS } from "consts";

import { Flex, Option, Select, Table, TagNormal, Tooltip, Typography } from "components";

import { SoilPropertyOriginTag, SoilPropertyOriginTagLegend } from "pages/admin/soilSight/components/shared/SoilPropertyOriginTag.tsx";
import { DeltaRow, LocationDeltaRow, LocationRotationDeltaRow } from "pages/admin/soilSight/utils/compareTypes.ts";
import { formatDelta, soilDeltaColor, valueColor } from "pages/admin/soilSight/utils/compareUtils.ts";
import { soilPropertyOrigin } from "pages/admin/soilSight/utils/soilPropertyOrigin.ts";
import { locationColorMap as buildLocationColorMap, scenarioColor } from "pages/admin/soilSight/utils/soilSightColors";

export const MultipleVersionTable = ({ deltas }: { deltas: DeltaRow[] }) => {
  const [filterLocation, setFilterLocation] = useState<string>("all");

  const locations = useMemo(() => [...new Set(deltas.map((d) => d.location))].sort(), [deltas]);
  const locationColors = useMemo(() => buildLocationColorMap(locations), [locations]);

  const filtered = filterLocation === "all" ? deltas : deltas.filter((d) => d.location === filterLocation);

  const mainColumns = [
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      fixed: "left" as const,
      sorter: (a: DeltaRow, b: DeltaRow) => a.location.localeCompare(b.location),
      render: (val: string) => (
        <span className="soil-sight-validation-overview-location-cell">
          <span className="soil-sight-validation-overview-location-dot" style={{ background: locationColors[val] ?? "#999" }} />
          {val}
        </span>
      ),
    },
    {
      title: "Rotation",
      dataIndex: "rotation",
      key: "rotation",
      fixed: "left" as const,
      sorter: (a: DeltaRow, b: DeltaRow) => a.rotation.localeCompare(b.rotation),
      render: (val: string, row: DeltaRow) => (row.rotationCrops ? <Tooltip title={row.rotationCrops}>{val}</Tooltip> : val),
    },
    {
      title: "Scenario",
      dataIndex: "scenario",
      key: "scenario",
      fixed: "left" as const,
      sorter: (a: DeltaRow, b: DeltaRow) => a.scenario.localeCompare(b.scenario),
      render: (val: string) => <TagNormal text={val} style={{ background: scenarioColor(val), color: "#fff", border: "none" }} />,
    },
    {
      title: "Yrs",
      dataIndex: "matchedYears",
      key: "matchedYears",
      sorter: (a: DeltaRow, b: DeltaRow) => a.matchedYears - b.matchedYears,
    },
    ...COMPARE_METRICS.map((metric) => ({
      title: (
        <div>
          <div>{metric.label}</div>
          <div className="soil-sight-table-th-unit">{metric.unit}</div>
        </div>
      ),
      key: metric.key,
      align: "right" as const,
      sorter: (a: DeltaRow, b: DeltaRow) => (a.deltas[metric.key] ?? -Infinity) - (b.deltas[metric.key] ?? -Infinity),
      render: (_: unknown, row: DeltaRow) => {
        const d = row.deltas[metric.key] ?? null;
        return <span style={{ color: valueColor(d, metric.higherIsBetter), fontWeight: d !== null && Math.abs(d) > 0.001 ? 600 : 400 }}>{formatDelta(d)}</span>;
      },
    })),
  ];

  const rowKey = (row: DeltaRow) => `${row.location}|${row.rotation}|${row.scenario}`;
  const rowClassName = (_: DeltaRow, index: number) => (index % 2 !== 0 ? "soil-sight-row--striped" : "");

  return (
    <div>
      <div className="soil-sight-table-toolbar">
        <Select label="Location" value={filterLocation} style={{ width: 160 }} onChange={(v: string) => setFilterLocation(v)}>
          <Option value="all">All</Option>
          {locations.map((location) => (
            <Option key={location} value={location}>
              {location}
            </Option>
          ))}
        </Select>
        <span className="soil-sight-table-hint">Values = avg delta per year (B minus A). Green = improvement.</span>
      </div>

      <Table data={filtered} columns={mainColumns} size="small" pagination={false} rowKey={rowKey} rowClassName={rowClassName} scroll={{ x: true }} />
    </div>
  );
};

export const MultipleVersionLocationTable = ({
  deltas,
  baselineVersion,
  candidateVersion,
}: {
  deltas: LocationDeltaRow[];
  baselineVersion: string | null;
  candidateVersion: string | null;
}) => {
  const locationColors = useMemo(() => buildLocationColorMap(deltas.map((d) => d.location)), [deltas]);

  const columns = [
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      fixed: "left" as const,
      sorter: (a: LocationDeltaRow, b: LocationDeltaRow) => a.location.localeCompare(b.location),
      render: (val: string) => (
        <span className="soil-sight-validation-overview-location-cell">
          <span className="soil-sight-validation-overview-location-dot" style={{ background: locationColors[val] ?? "#999" }} />
          {val}
        </span>
      ),
    },
    ...LOCATION_SOIL_METRICS.map((metric) => ({
      title: (
        <Flex vertical align="center" justify="flex-start">
          <Flex gap="6px" align="center" justify="flex-start">
            <SoilPropertyOriginTag
              origin={soilPropertyOrigin(metric.key, baselineVersion)}
              candidateOrigin={soilPropertyOrigin(metric.key, candidateVersion)}
            />
            <div>{metric.label}</div>
          </Flex>
          {metric.unit && <div className="soil-sight-table-th-unit">{metric.unit}</div>}
        </Flex>
      ),
      key: metric.key,
      align: "right" as const,
      sorter: (a: LocationDeltaRow, b: LocationDeltaRow) => (a.deltas[metric.key] ?? -Infinity) - (b.deltas[metric.key] ?? -Infinity),
      render: (_: unknown, row: LocationDeltaRow) => {
        const d = row.deltas[metric.key] ?? null;
        return <span style={{ color: soilDeltaColor(d), fontWeight: d !== null && Math.abs(d) > 0.0001 ? 600 : 400 }}>{formatDelta(d)}</span>;
      },
    })),
  ];

  return (
    <div>
      <div className="soil-sight-table-toolbar">
        <Flex vertical>
          <Typography variant="h3">Soil/weather properties per location</Typography>
          <span className="soil-sight-table-hint"> Orange = differs.</span>
        </Flex>
        <SoilPropertyOriginTagLegend />
      </div>
      <Table data={deltas} columns={columns} size="small" pagination={false} rowKey="location" scroll={{ x: true }} />
    </div>
  );
};

export const MultipleVersionLocationRotationTable = ({
  deltas,
  baselineVersion,
  candidateVersion,
}: {
  deltas: LocationRotationDeltaRow[];
  baselineVersion: string | null;
  candidateVersion: string | null;
}) => {
  const locations = useMemo(() => [...new Set(deltas.map((d) => d.location))].sort(), [deltas]);
  const locationColors = useMemo(() => buildLocationColorMap(locations), [locations]);

  const columns = [
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      fixed: "left" as const,
      sorter: (a: LocationRotationDeltaRow, b: LocationRotationDeltaRow) => a.location.localeCompare(b.location),
      render: (val: string) => (
        <span className="soil-sight-validation-overview-location-cell">
          <span className="soil-sight-validation-overview-location-dot" style={{ background: locationColors[val] ?? "#999" }} />
          {val}
        </span>
      ),
    },
    {
      title: "Rotation",
      dataIndex: "rotation",
      key: "rotation",
      fixed: "left" as const,
      sorter: (a: LocationRotationDeltaRow, b: LocationRotationDeltaRow) => a.rotation.localeCompare(b.rotation),
      render: (val: string, row: LocationRotationDeltaRow) => (row.rotationCrops ? <Tooltip title={row.rotationCrops}>{val}</Tooltip> : val),
    },
    ...CROP_SOIL_METRICS.map((metric) => ({
      title: (
        <Flex vertical align="center" justify="flex-start">
          <Flex gap="6px" align="center" justify="flex-start">
            <SoilPropertyOriginTag
              origin={soilPropertyOrigin(metric.key, baselineVersion)}
              candidateOrigin={soilPropertyOrigin(metric.key, candidateVersion)}
            />

            <div>{metric.label}</div>
          </Flex>
          {metric.unit && <div className="soil-sight-table-th-unit">{metric.unit}</div>}
        </Flex>
      ),
      key: metric.key,
      align: "right" as const,
      sorter: (a: LocationRotationDeltaRow, b: LocationRotationDeltaRow) => (a.deltas[metric.key] ?? -Infinity) - (b.deltas[metric.key] ?? -Infinity),
      render: (_: unknown, row: LocationRotationDeltaRow) => {
        const d = row.deltas[metric.key] ?? null;
        const newVal = row.newValues[metric.key] ?? null;
        if (d !== null) {
          return <span style={{ color: soilDeltaColor(d), fontWeight: Math.abs(d) > 0.0001 ? 600 : 400 }}>{formatDelta(d)}</span>;
        }
        if (newVal !== null) {
          return (
            <Tooltip title="New in v4 — no v3 baseline to diff against">
              <span style={{ color: "#2563eb", fontWeight: 600 }}>{newVal.toFixed(2)}</span>
              <span style={{ display: "block", fontSize: "0.625rem", color: "#93c5fd", letterSpacing: "0.02em" }}>new</span>
            </Tooltip>
          );
        }
        return <span style={{ color: "#d1d5db" }}>—</span>;
      },
    })),
  ];

  const rowKey = (row: LocationRotationDeltaRow) => `${row.location}|${row.rotation}`;

  return (
    <div>
      <div className="soil-sight-table-toolbar">
        <Flex vertical>
          <Typography variant="h3">Soil/weather properties per location</Typography>
          <span className="soil-sight-table-hint">— vary by the rotation&apos;s first crop, one row per location × rotation.</span>
        </Flex>
      </div>
      <Table data={deltas} columns={columns} size="small" pagination={false} rowKey={rowKey} scroll={{ x: true }} />
    </div>
  );
};
